import { Capacitor } from '@capacitor/core';
import { IonCard, IonCol, IonGrid, IonRefresher, IonRefresherContent, IonRippleEffect, IonRow, IonText, RefresherEventDetail } from '@ionic/react';
import { arrowDownOutline } from 'ionicons/icons';
import { useState } from 'react';
import { useHistory } from 'react-router';

import { logTransaction } from '../../common/log';
import { ClockingDirection } from '../../models/dms/clocking.model';
import { LogType } from '../../models/log.model';
import { RouteNames } from '../../models/routes.model';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useLocation } from '../../providers/LocationProvider';
import store from '../../store';
import { setAllowFetchClockingsState, setAllowOverrideAttendanceState, setAllowOverrideLabourState } from '../../store/appSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import AttendanceClockingCard from '../attendanceClockingCard/AttendanceClockingCard';
import { DashboardSelectedTab } from '../dashboardFooter/DashboardFooter';
import DistanceTrackingCard from '../distanceTrackingCard/DistanceTrackingCard';
import LabourClockingCardContent from '../labourClockingCard/labourClockingCardContent/LabourClockingCardContent';
import LabourClockingCardHeader from '../labourClockingCard/labourClockingCardHeader/LabourClockingCardHeader';
import ProfileHeader from '../profileHeader/ProfileHeader';
import { IonContentWrapper } from './ClockingSummary.styles';

interface IComponentProps { }

const ClockingSummary: React.FC<IComponentProps> = () => {
  const [platform] = useState(Capacitor.getPlatform());
  const history = useHistory();

  const { labourClocking } = useAppSelector(state => state.technicianClocking);
  const { distanceTrackingUserActivated, isDarkMode } = useAppSelector(state => state.settings);
  const { isDemo } = useAppSelector(state => state.app);
  const { state: { clockingDetailsSyncTimer } } = useDataSync();
  const { actions: { startDistanceTracking } } = useLocation();

  const dispatch = useAppDispatch();

  const doRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await dispatch(setAllowFetchClockingsState(true));
    await dispatch(setAllowOverrideAttendanceState(true));
    await dispatch(setAllowOverrideLabourState(true));
    clockingDetailsSyncTimer?.fire();
    // Add a fake "loading" progress to make it look like the app is working ;)
    setTimeout(() => {
      event.detail.complete();
    }, 1000);
  };

  const handleClickLabourClockingCard = () => {
    const { labourClocking } = store.getState().technicianClocking;

    if (labourClocking?.direction === ClockingDirection.On || labourClocking?.direction === ClockingDirection.Break) {
      history.push({
        pathname: RouteNames.clockingDetails
      });
    } else {
      history.push({
        pathname: RouteNames.dashboard,
        search: `tab=${DashboardSelectedTab.JobSearch}`
      });
    }
  };

  const handleEnableDistanceTrackingClick = async () => {
    logTransaction(LogType.Info, `Enable distance tracking requested`);
    await startDistanceTracking();
  };

  return (
    <>
      <ProfileHeader title="Dashboard" />
      <IonContentWrapper color="lightest">
        <IonRefresher slot="fixed" onIonRefresh={(event) => doRefresh(event)}>
          <IonRefresherContent pullingIcon={arrowDownOutline} pullingText="" refreshingSpinner={'lines'} refreshingText="" />
        </IonRefresher>
        <IonGrid>
          <IonRow>
            <IonCol sizeXs="12" sizeSm="12" sizeMd="6" sizeLg="6" sizeXl="6" className="flexed">
              <AttendanceClockingCard simpleType={false} />
            </IonCol>
            <IonCol sizeXs="12" sizeSm="12" sizeMd="6" sizeLg="6" sizeXl="6" className="flexed">
              <IonCard className="ion-activatable ripple-card clocking-card" onClick={() => handleClickLabourClockingCard()}>
                <IonRippleEffect type="bounded" />
                <LabourClockingCardHeader />
                <LabourClockingCardContent />
              </IonCard>
            </IonCol>
          </IonRow>

          {labourClocking?.direction === ClockingDirection.On &&
            <IonRow>
              <IonCol size="12" className="flexed">
                {!distanceTrackingUserActivated &&
                  <IonCard className="clocking-card flexed col ion-activatable ripple-card clocking-card" onClick={() => handleEnableDistanceTrackingClick()}>
                    <IonRippleEffect type="bounded" />
                    <div className="flexed col">
                      <div className="flexed button-display">
                        <div className="flexed-fill flexed flexed-center ion-margin">
                          <IonText className="flexed-fill">
                            <h6 className="ion-text-center">Record Distance</h6>
                          </IonText>
                        </div>
                        <div className="distance-container ion-margin">
                          {!isDarkMode && <div className="distance">
                            0<div className="unit">m</div>
                          </div>}
                          {isDarkMode && <div className="distance dark">
                            0<div className="unit">m</div>
                          </div>}
                        </div>
                      </div>
                    </div>
                  </IonCard>}
                {(platform !== 'web' || isDemo) && distanceTrackingUserActivated && <DistanceTrackingCard />}
              </IonCol>
            </IonRow>}
        </IonGrid>
      </IonContentWrapper>
    </>
  );
};

export default ClockingSummary;